import type { Options } from './wasmBridge/viewModel';

export interface Preset {
  options: Options[];
}

function canonical(options: Options[]): Options[] {
  return [...options].sort((a, b) => a.index - b.index);
}

export function sameOptions(a: Options[], b: Options[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const first = canonical(a);
  const second = canonical(b);

  return first.every((option, position) => {
    const other = second[position];
    return option.index === other.index && option.type === other.type && option.value === other.value;
  });
}

export function appendPreset(presets: Preset[], preset: Preset): Preset[] | null {
  if (presets.some(existing => sameOptions(existing.options, preset.options))) {
    return null;
  }

  return [...presets, preset];
}

export function removePreset(presets: Preset[], options: Options[]): Preset[] {
  return presets.filter(existing => !sameOptions(existing.options, options));
}

// Encode a saved preset's options so the legacy gameplay page can identify and
// apply it via `?saved=` (BR-50). Presets are keyed by options (no stored name),
// so the options themselves are the stable identifier.
export function encodePresetOptions(options: Options[]): string {
  return encodeURIComponent(JSON.stringify(options));
}

const OPTION_TYPES: readonly Options['type'][] = ['number', 'boolean', 'choices'];

// A crafted `?saved=` query reaches sameOptions, which dereferences
// option.index — reject anything that isn't a well-formed option first.
function isValidOption(value: unknown): value is Options {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const option = value as Partial<Options>;
  if (!Number.isInteger(option.index) || (option.index as number) < 0) {
    return false;
  }
  if (typeof option.type !== 'string' || !OPTION_TYPES.includes(option.type as Options['type'])) {
    return false;
  }
  if (option.type === 'boolean') {
    return typeof option.value === 'boolean';
  }
  return typeof option.value === 'number' && Number.isFinite(option.value);
}

/** True when `value` is a well-formed Options[] (used to validate stored records). */
export function isValidPresetOptions(value: unknown): value is Options[] {
  return Array.isArray(value) && value.every(isValidOption);
}

export function decodePresetOptions(value: string): Options[] | null {
  try {
    const parsed = JSON.parse(decodeURIComponent(value));
    if (!Array.isArray(parsed) || !parsed.every(isValidOption)) {
      return null;
    }
    return [...parsed];
  } catch {
    return null;
  }
}
